/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {computed, Injectable, signal} from '@angular/core';
import {MessageService, ToastMessageOptions} from 'primeng/api';
import {ErrorMessage} from '../model/errorMessage';
import {ResponseWrapper} from '../model/responseWrapper';
import {HttpErrorResponse} from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class EsciMessageService {

    /**
     * Dedicated key for the global toast stack, matching the `key="global"` attribute on the
     * single <p-toast> in app.component.html. Without a shared key, PrimeNG's MessageService
     * broadcasts unkeyed messages to *every* unkeyed <p-toast> mounted anywhere in the app
     * (including local, unrelated toasts in routed components), causing duplicate rendering and
     * desyncing {@link activeToastCount} whenever a toast is closed/expires via one of those other
     * instances instead of the global one.
     */
    private static readonly GLOBAL_TOAST_KEY = 'global';

    constructor(private messageService: MessageService) {
    }

    /**
     * Count of toasts currently in the global (`key: 'global'`) toast stack. Drives
     * {@link showCloseAll}. Incremented by {@link addToast}; decremented by {@link onToastClosed}
     * (bound to the global <p-toast>'s (onClose) output, which fires for every removal path:
     * manual close, timeout expiry, or clear-all).
     */
    private readonly activeToastCount = signal(0);

    /** True once 2+ global toasts are visible, so a "Close All" action becomes worthwhile. */
    public readonly showCloseAll = computed(() => this.activeToastCount() > 1);

    /**
     * Single, generic entry point for adding a message to the global toast stack. Every
     * severity-producing method below routes through this instead of hand-building its own
     * toast object, so error/success toast behavior stays on one shared code path.
     * @param options.sticky When true, the toast persists until the user closes it (no auto-dismiss).
     * @param options.life   When set (and not sticky), PrimeNG removes the toast after this many ms.
     *                       When neither is set, the toast's own default life is used.
     */
    private addToast(severity: string, summary: string, detail: string, options?: { life?: number; sticky?: boolean }) {
        const toast: ToastMessageOptions = {key: EsciMessageService.GLOBAL_TOAST_KEY, severity, summary, detail, closable: true};
        if (options?.sticky) {
            toast.sticky = true;
        } else if (options?.life != null) {
            toast.life = options.life;
        }
        this.activeToastCount.update(count => count + 1);
        this.messageService.add(toast);
    }

    /** Decrements the active toast count. Bound to the global <p-toast>'s (onClose) output. */
    public onToastClosed(): void {
        this.activeToastCount.update(count => Math.max(0, count - 1));
    }

    public processSuccess(successMessage: string) {
        this.processSuccessWithoutClear(successMessage, 4000);
    }

    /**
     * @param lifeMs When set, PrimeNG removes the toast after this many ms (replaces manual clear + setTimeout).
     */
    public processSuccessWithoutClear(successMessage: string, lifeMs?: number) {
        if (successMessage != null) {
            this.addToast('success', 'Success', successMessage, lifeMs != null ? {life: lifeMs} : undefined);
        }
    }

    public processError(errorMessage: string) {
        if (errorMessage != null) {
            console.error(errorMessage);
            this.addToast('error', 'Error', errorMessage, {sticky: true});
        }
    }

    public processInlineSuccess(msg: string, key: string): void {
        if (msg != null) {
            this.messageService.add({key, severity: 'success', summary: 'Success', detail: msg});
        }
    }

    public processInlineError(msg: string, key: string): void {
        if (msg != null) {
            console.error(msg);
            this.messageService.add({key, severity: 'error', summary: 'Error', detail: msg});
        }
    }

    public clearInline(key: string): void {
        this.messageService.clear(key);
    }

    public processErrorMessage(errorMessage: ErrorMessage) {
        this.addToast('error', errorMessage.description, errorMessage.solution, {sticky: true});
    }

    public processResponseWrapperErrorMessage(response: ResponseWrapper) {
        if (response.success === false && response.errorMessages != null) {
            this.processErrorMessage(response.errorMessages);
        }
    }

    public processException(error: Error) {
        if (error != null) {
            if (error instanceof HttpErrorResponse) { // Assuming 'error' is an HttpErrorResponse
                if (error.error != null && error.error.errorMessages != null) {
                    this.processErrorMessage(error.error.errorMessages[0]);
                } else {
                    this.addToast('error', 'Error', error.message, {sticky: true});
                }
            } else {
                // Handle the case where 'error' is not an HttpErrorResponse
                console.error('Invalid error type:', error);
            }
        }
    }

    public clear() {
        this.messageService.clear(EsciMessageService.GLOBAL_TOAST_KEY);
        this.activeToastCount.set(0);
    }

}
